import {
	getFirestore as mGetFirestore,
	collection as mCollection,
	doc as mDoc,
	query as mQuery,
	where as mWhere,
	orderBy as mOrderBy,
	limit as mLimit,
	startAfter as mStartAfter,
	getDoc,
	getDocs,
	onSnapshot as mOnSnapshot,
	setDoc,
	updateDoc,
	deleteDoc,
	writeBatch,
	runTransaction as mRunTransaction,
	serverTimestamp,
	increment,
	arrayUnion,
	arrayRemove,
	deleteField,
	documentId,
	Timestamp,
	type Firestore,
	type DocumentData,
	type DocumentReference as MDocRef,
	type CollectionReference as MCollRef,
	type Query as MQueryType,
	type DocumentSnapshot as MDocSnap,
	type QuerySnapshot as MQuerySnap,
	type Transaction as MTransaction,
	type WriteBatch as MWriteBatch,
	type SetOptions,
	type WhereFilterOp,
	type OrderByDirection,
	type FirestoreError,
	type FieldPath as MFieldPath,
} from "firebase/firestore";
import type { FirebaseApp } from "firebase/app";

/*
 * Stands in for `@react-native-firebase/firestore`.
 *
 * The service layer in ../../src/services is shared verbatim with the mobile
 * app and is written against RNFirebase's chaining API
 * (`db.collection(x).where(...).limit(n).get()`). This module re-exposes that
 * API over the Firebase web SDK's modular functions.
 *
 * WHY MODULAR AND NOT `firebase/compat`, which offers the same chaining shape:
 *
 *   1. `.exists`. RNFirebase v23 declares `exists(): boolean` — a METHOD — and
 *      the services call it as one in 23 places. compat declares it as a
 *      readonly PROPERTY, so `doc.exists()` there is "not a function" and every
 *      missing-document check throws. tools/check-layering.sh rule 6 exists to
 *      police exactly this. The modular SDK also declares it as a method, so
 *      the two agree.
 *
 *   2. Named databases. Under __DEV__ the app talks to a database literally
 *      named "test" (src/constants/database.ts). compat's accessor takes no
 *      databaseId and can only ever return "(default)" — a dev build would
 *      silently read and write PRODUCTION. Modular has
 *      getFirestore(app, databaseId).
 *
 * The wrapper classes below carry a `_` handle to the modular object they wrap.
 * Unwrapping happens at exactly the points where a wrapped value is handed back
 * into the modular SDK: batch/transaction writes, and startAfter cursors.
 *
 * SCOPE. This covers what the services actually use and nothing more — no
 * .add(), no collectionGroup(), no .metadata, no GetOptions. If a service grows
 * a new call, add it here; do not work around it at the call site, because the
 * mobile app has to keep running the same line of code.
 */

/* ------------------------------------------------------------- snapshots */

export class DocSnap {
	constructor(readonly _s: MDocSnap<DocumentData>) {}

	get id() {
		return this._s.id;
	}

	get ref(): DocRef {
		return new DocRef(this._s.ref);
	}

	/** A METHOD, matching RNFirebase v23. See the note at the top of the file. */
	exists(): boolean {
		return this._s.exists();
	}

	data(): DocumentData | undefined {
		return this._s.data();
	}

	get(fieldPath: string): unknown {
		return this._s.get(fieldPath);
	}
}

export class QuerySnap {
	readonly docs: DocSnap[];

	constructor(readonly _s: MQuerySnap<DocumentData>) {
		this.docs = _s.docs.map((d) => new DocSnap(d));
	}

	get empty() {
		return this._s.empty;
	}

	get size() {
		return this._s.size;
	}

	forEach(fn: (doc: DocSnap) => void): void {
		this.docs.forEach(fn);
	}
}

/* --------------------------------------------------------------- queries */

export class Q {
	constructor(readonly _q: MQueryType<DocumentData>) {}

	/**
	 * `fieldPath` accepts a FieldPath as well as a string —
	 * `where(firestore.FieldPath.documentId(), "in", ids)` is how
	 * companyService, libraryService and eventService fetch documents by id.
	 */
	where(
		fieldPath: string | MFieldPath,
		opStr: WhereFilterOp,
		value: unknown,
	): Q {
		return new Q(mQuery(this._q, mWhere(fieldPath, opStr, value)));
	}

	orderBy(
		fieldPath: string | MFieldPath,
		directionStr?: OrderByDirection,
	): Q {
		return new Q(mQuery(this._q, mOrderBy(fieldPath, directionStr)));
	}

	limit(n: number): Q {
		return new Q(mQuery(this._q, mLimit(n)));
	}

	/**
	 * Accepts the wrapped DocSnap the services carry around as a page cursor
	 * (timeEntryService.getTimeEntries returns one), and unwraps it.
	 */
	startAfter(snapshot: DocSnap | unknown): Q {
		const cursor =
			snapshot instanceof DocSnap ? snapshot._s : (snapshot as never);
		return new Q(mQuery(this._q, mStartAfter(cursor)));
	}

	async get(): Promise<QuerySnap> {
		return new QuerySnap(await getDocs(this._q));
	}

	onSnapshot(
		onNext: (snapshot: QuerySnap) => void,
		onError?: (error: FirestoreError) => void,
	): () => void {
		return mOnSnapshot(
			this._q,
			(snapshot) => onNext(new QuerySnap(snapshot)),
			onError ?? (() => {}),
		);
	}
}

/* ------------------------------------------------------------ references */

export class DocRef {
	constructor(readonly _r: MDocRef<DocumentData>) {}

	get id() {
		return this._r.id;
	}

	get path() {
		return this._r.path;
	}

	/** Subcollections — timeEntries/{id}/connections and /edits. */
	collection(collectionPath: string): CollRef {
		return new CollRef(mCollection(this._r, collectionPath));
	}

	async get(): Promise<DocSnap> {
		return new DocSnap(await getDoc(this._r));
	}

	/** Supports both `{merge}` and `{mergeFields}` (eventChecklistService). */
	set(data: DocumentData, options?: SetOptions): Promise<void> {
		return options ? setDoc(this._r, data, options) : setDoc(this._r, data);
	}

	/** Dotted field paths pass straight through, as they do in RNFirebase. */
	update(data: DocumentData): Promise<void> {
		return updateDoc(this._r, data);
	}

	delete(): Promise<void> {
		return deleteDoc(this._r);
	}

	onSnapshot(
		onNext: (snapshot: DocSnap) => void,
		onError?: (error: FirestoreError) => void,
	): () => void {
		return mOnSnapshot(
			this._r,
			(snapshot) => onNext(new DocSnap(snapshot)),
			onError ?? (() => {}),
		);
	}
}

export class CollRef extends Q {
	constructor(readonly _c: MCollRef<DocumentData>) {
		super(_c);
	}

	/** No argument generates an auto-id, matching RNFirebase. */
	doc(documentPath?: string): DocRef {
		return new DocRef(
			documentPath ? mDoc(this._c, documentPath) : mDoc(this._c),
		);
	}
}

/* ------------------------------------------------------ batch/transaction */

export class Batch {
	constructor(private readonly _b: MWriteBatch) {}

	set(ref: DocRef, data: DocumentData, options?: SetOptions): Batch {
		if (options) this._b.set(ref._r, data, options);
		else this._b.set(ref._r, data);
		return this;
	}

	update(ref: DocRef, data: DocumentData): Batch {
		this._b.update(ref._r, data);
		return this;
	}

	delete(ref: DocRef): Batch {
		this._b.delete(ref._r);
		return this;
	}

	commit(): Promise<void> {
		return this._b.commit();
	}
}

export class Tx {
	constructor(private readonly _t: MTransaction) {}

	async get(ref: DocRef): Promise<DocSnap> {
		return new DocSnap(await this._t.get(ref._r));
	}

	set(ref: DocRef, data: DocumentData, options?: SetOptions): Tx {
		if (options) this._t.set(ref._r, data, options);
		else this._t.set(ref._r, data);
		return this;
	}

	update(ref: DocRef, data: DocumentData): Tx {
		this._t.update(ref._r, data);
		return this;
	}

	delete(ref: DocRef): Tx {
		this._t.delete(ref._r);
		return this;
	}
}

/* --------------------------------------------------------- the db handle */

export class Db {
	constructor(readonly _f: Firestore) {}

	collection(collectionPath: string): CollRef {
		return new CollRef(mCollection(this._f, collectionPath));
	}

	doc(documentPath: string): DocRef {
		return new DocRef(mDoc(this._f, documentPath));
	}

	batch(): Batch {
		return new Batch(writeBatch(this._f));
	}

	runTransaction<T>(updateFunction: (tx: Tx) => Promise<T>): Promise<T> {
		return mRunTransaction(this._f, (t) => updateFunction(new Tx(t)));
	}
}

/**
 * Mirrors the two call shapes in src/lib/db.ts exactly:
 *   getFirestore(app)              → "(default)"
 *   getFirestore(app, DATABASE_ID) → a named database
 */
export function getFirestore(app: FirebaseApp, databaseId?: string): Db {
	return new Db(
		databaseId ? mGetFirestore(app, databaseId) : mGetFirestore(app),
	);
}

/* ----------------------------------------------- the `firestore.*` statics */

/*
 * Services do `import firestore from "@react-native-firebase/firestore"` and
 * then reach for firestore.FieldValue / FieldPath / Timestamp. The modular
 * equivalents are plain functions, so these namespaces just re-bundle them.
 */
export const FieldValue = {
	serverTimestamp,
	increment,
	arrayUnion,
	arrayRemove,
	delete: deleteField,
};

export const FieldPath = {
	documentId,
};

export { Timestamp };

export default { FieldValue, FieldPath, Timestamp };

/* -------------------------------------------------------- type namespace */

export type { FirebaseFirestoreTypes } from "./firestore-types";
